import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { arrayOf, func, shape } from 'prop-types';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import ProductDetails from './ProductDetails';
import { renderInput } from '../../utils/renderUtils';
import { useGetProductsQuery } from '../../customerAuthorizationsEndpoints';

export const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.productNameShort}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Products = ({ info, setInfo }) => {
  const { customerId: customerIdName, products = [], terminals = [] } = info;
  const isAuthSpecific = R.equals(R.prop('authorizationType', info), 'SPECIFIC');

  const { currentAffiliate = {} } = useUserRolePermissions();
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;

  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
    terminals: R.map(R.prop('terminalId'), terminals),
  };

  const { data: productsQuery = [], isFetching: isFetchingProducts } = useGetProductsQuery(
    { params },
    {
      skip: R.isEmpty(terminals),
    }
  );

  const handleChange = (_, value, reason, detail) => {
    if (reason === 'removeOption') {
      const { option } = detail;
      const filtered = products.filter(({ productId }) => productId !== option.productId);
      // as products from edit customer auth are a diff obj from get products
      // if you try to remove one that was previously selected from edit customer
      // then value will be [] due to not exact matching the list
      const newProducts = R.equals(value, []) ? filtered : value;
      setInfo(R.assoc('products', newProducts, info));
    } else if (reason === 'clear') {
      setInfo(R.assoc('products', [], info));
    } else {
      setInfo(R.assoc('products', value, info));
    }
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        aria-controls="customer-customerAuth-content"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">
            Products ({isAuthSpecific ? 'Required' : 'Optional'})
          </Typography>
          {!!R.prop('products', info)?.length && (
            <Chip label={R.prop('products', info)?.length} type="accordion" variant="badge" />
          )}
          {R.and(R.isEmpty(products), isAuthSpecific) && (
            <ErrorRoundedIcon sx={{ color: 'error.main' }} />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Typography>
            If you have no products selected, you will allow all products for this authorization. To
            limit which products can be loaded, change the Authorization Type to Product Specific
            and add one or more product(s).
          </Typography>
          <Autocomplete
            getOptionLabel={R.compose(R.join(' '), R.props(['productIdName', 'productNameShort']))}
            isOptionEqualToValue={R.eqProps('productId')}
            limitTags={3}
            loading={isFetchingProducts}
            multiple
            onChange={handleChange}
            options={productsQuery}
            renderInput={renderInput({
              label: 'Search Products',
              length: R.length(products),
              loading: isFetchingProducts,
            })}
            renderTags={renderTags}
            slotProps={{
              chip: {
                variant: 'selection',
              },
            }}
            value={R.defaultTo([], products)}
          />
          <Stack>
            {products.map(product => (
              <ProductDetails
                info={info}
                key={product.productId}
                product={product}
                setInfo={setInfo}
              />
            ))}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

Products.propTypes = {
  info: shape({
    products: arrayOf(shape({})),
  }),
  setInfo: func.isRequired,
};

Products.defaultProps = {
  info: {
    products: [],
  },
};

export default Products;
